import { useRef, useState } from "react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import { cn } from "@/lib/utils"
import { useDarkMode } from "@/lib/useDarkMode"
import { useCurrentUser } from "@/lib/useCurrentUser"
import { isSurfaceEnabled } from "@/lib/ui-control-plane/runtimeAvailability"
import type { SurfaceId } from "@/lib/ui-control-plane"
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  Sparkles,
  User,
  LogOut,
  Users,
  Landmark,
  ShieldCheck,
  Database,
  Briefcase,
  ClipboardList,
  ArrowDownUp,
  Truck,
  ScrollText,
  Activity,
  Monitor,
  KeyRound,
  Shield,
  UserCog,
  Tags,
  Bot,
  Gauge,
  FlaskConical,
  LayoutDashboard,
  Workflow,
  BadgeCheck,
} from "lucide-react"
import { AGENT_CONSOLE_GROUPS, AGENT_VIEW_LABEL, agentPathForView } from "@/lib/agents-nav"

// Icon per Agentic Console group (sidebar sub-tabs when that section is active).
const AGENTS_GROUP_ICONS: Record<string, React.ElementType> = {
  "Overview": LayoutDashboard,
  "Definitions": ScrollText,
  "Activity": Activity,
  "Operations": Workflow,
  "Governance & Security": ShieldCheck,
  "Cost & Budgeting": Gauge,
  "Testing": FlaskConical,
  "Quality": BadgeCheck,
}

interface SubPage {
  to: string
  label: string
  icon: React.ElementType
  surfaceId?: SurfaceId
  /** The Agentic Console surface keys this entry owns (the last /agents/<view>
   *  path segment). A group is active when the current surface is one of them. */
  matchTab?: string[]
  /** Nested children for expandable submenus. icon optional; matchTab resolves
   *  the active surface for /agents/<view> leaves. */
  children?: { to: string; label: string; icon?: React.ElementType; surfaceId?: SurfaceId; matchTab?: string[] }[]
}

interface NavSection {
  id: string
  label: string
  icon: React.ElementType
  basePath: string
  landingPage?: string
  subPages: SubPage[]
  adminOnly?: boolean
}

const navSections: NavSection[] = [
  // Main entity surfaces — order: Companies, People, Investors
  // No subPages = renders as direct nav link (no expandable children)
  {
    id: "companies",
    label: "Companies",
    icon: Building2,
    basePath: "/companies",
    landingPage: "/companies",
    subPages: [],
  },
  {
    id: "people",
    label: "People",
    icon: Users,
    basePath: "/people",
    landingPage: "/people",
    subPages: [],
  },
  {
    id: "investors",
    label: "Investors",
    icon: Landmark,
    basePath: "/investors",
    landingPage: "/investors",
    subPages: [],
  },
  {
    id: "up",
    label: "Unicorn Prediction",
    icon: Sparkles,
    basePath: "/up",
    landingPage: "/up",
    subPages: [],
  },
  {
    id: "tags",
    label: "Tagging",
    icon: Tags,
    basePath: "/tags",
    landingPage: "/tags",
    subPages: [],
  },
  {
    id: "agents",
    label: "Agentic Console",
    icon: Bot,
    basePath: "/agents",
    landingPage: "/agents",
    // Console groups become sidebar sub-tabs (shown only when Agentic Console
    // is active); the active group expands to its surfaces — same drill-down
    // depth as Admin's Master Lists → Companies/People/Investors.
    subPages: AGENT_CONSOLE_GROUPS.map((g) => ({
      to: agentPathForView(g.views[0]),
      label: g.label,
      icon: AGENTS_GROUP_ICONS[g.label] ?? Bot,
      matchTab: g.views,
      children: g.views.length > 1
        ? g.views.map((v) => ({ to: agentPathForView(v), label: AGENT_VIEW_LABEL[v] ?? v, matchTab: [v] }))
        : undefined,
    })),
  },
  // Admin Console — admin-only, backend-supported surfaces
  {
    id: "admin",
    label: "Admin Console",
    icon: ShieldCheck,
    basePath: "/admin",
    landingPage: "/admin/master-lists/companies",
    adminOnly: true,
    subPages: [
      {
        to: "/admin/master-lists",
        label: "Master Lists",
        icon: Database,
        children: [
          { to: "/admin/master-lists/companies", label: "Companies", icon: Building2, surfaceId: "admin_master_companies" },
          { to: "/admin/master-lists/people", label: "People", icon: Users, surfaceId: "admin_master_people" },
          { to: "/admin/master-lists/investors", label: "Investors", icon: Landmark, surfaceId: "admin_master_investors" },
        ],
      },
      {
        to: "/admin/jobs",
        label: "Jobs & Operations",
        icon: Briefcase,
        children: [
          { to: "/admin/jobs/imports", label: "Imports", icon: ArrowDownUp },
          { to: "/admin/jobs/exports", label: "Exports", icon: ClipboardList },
          { to: "/admin/jobs/ingestion", label: "Ingestion", icon: Truck },
        ],
      },
      {
        to: "/admin/audit",
        label: "Audit",
        icon: ScrollText,
        children: [
          { to: "/admin/audit/actions", label: "Actions", icon: Activity },
          { to: "/admin/audit/sessions", label: "Sessions", icon: Monitor },
        ],
      },
      {
        to: "/admin/user-management",
        label: "User Management",
        icon: UserCog,
        children: [
          { to: "/admin/user-management/permissions", label: "Permissions", icon: KeyRound },
          { to: "/admin/user-management/roles", label: "Roles", icon: Shield },
          { to: "/admin/user-management/users", label: "Users", icon: Users },
        ],
      },
    ],
  },
]

/**
 * Filter sub-pages by runtime surface availability.
 * For items with children, filter children individually and remove parent if none remain.
 */
function filterEnabledSubPages(subPages: SubPage[]): SubPage[] {
  return subPages
    .map((sp) => {
      if (sp.children) {
        const enabled = sp.children.filter(
          (c) => !c.surfaceId || isSurfaceEnabled(c.surfaceId)
        )
        if (enabled.length === 0) return null
        return { ...sp, children: enabled }
      }
      return !sp.surfaceId || isSurfaceEnabled(sp.surfaceId) ? sp : null
    })
    .filter((sp): sp is SubPage => sp !== null)
}

/** Pre-filter navSections by surface availability (static at module load). */
const availableSections = navSections.map((s) => ({
  ...s,
  subPages: filterEnabledSubPages(s.subPages),
}))

interface SidebarProps {
  onLogoutClick: () => void
  onProfileClick: () => void
}

export function Sidebar({ onLogoutClick, onProfileClick }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [hoveredSection, setHoveredSection] = useState<string | null>(null)
  const hoverTimeoutRef = useRef<number | null>(null)

  const location = useLocation()
  const navigate = useNavigate()
  const { isDark, toggle } = useDarkMode()
  const { loaded: userLoaded, isAdmin } = useCurrentUser()

  const filteredSections = availableSections.filter(
    (section) => !section.adminOnly || (userLoaded && isAdmin)
  )

  const isPathActive = (basePath: string) => location.pathname.startsWith(basePath)

  const isSubPageActive = (to: string) => {
    return location.pathname === to || location.pathname.startsWith(to + "/")
  }

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }

  const handleMouseEnter = (sectionId: string) => {
    if (!isCollapsed) return
    clearHoverTimeout()
    setHoveredSection(sectionId)
  }

  const handleMouseLeave = () => {
    if (!isCollapsed) return
    clearHoverTimeout()
    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredSection(null)
    }, 150)
  }

  const handleFlyoutMouseEnter = () => {
    clearHoverTimeout()
  }

  const handleFlyoutMouseLeave = () => {
    clearHoverTimeout()
    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredSection(null)
    }, 150)
  }
  return (
    <aside
      className={cn(
        "relative flex h-full flex-col border-r border-border/40 bg-card transition-all duration-300",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      {/* Header — same h-14 + border-border/40 as the top nav so the divider
          reads as one continuous line in both states. Collapsed shows a single
          centered expand arrow (clear, easy target) — no cramming at w-16. */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-border/40 px-4">
        {isCollapsed ? (
          // Logo (brand, shown in both states) + an explicit expand arrow,
          // centered together so neither is cramped at w-16.
          <div className="flex w-full items-center justify-center gap-1">
            <img src={isDark ? "/darkmode.svg" : "/loginlogo.svg"} alt="HPS" className="h-6 w-6 shrink-0" />
            <button
              type="button"
              onClick={() => { setIsCollapsed(false); setHoveredSection(null) }}
              title="Expand sidebar"
              aria-label="Expand sidebar"
              className="flex h-7 w-5 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 truncate">
              <img src={isDark ? "/darkmode.svg" : "/loginlogo.svg"} alt="HPS" className="h-6 w-6 shrink-0" />
              <h1 className="text-sm font-semibold tracking-[-0.01em] truncate">HPS</h1>
            </div>
            <button
              type="button"
              onClick={() => { setIsCollapsed(true); setHoveredSection(null) }}
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* Navigation */}
      {/* Key fix: allow flyout to render outside the scroll container */}
      <nav className="flex-1 overflow-visible p-2">
        {filteredSections.map((section) => (
          <div
            key={section.id}
            className="relative mb-1"
            onMouseEnter={() => handleMouseEnter(section.id)}
            onMouseLeave={handleMouseLeave}
          >
            {/* Section header / collapsed icon */}
            {isCollapsed ? (
              <button
                type="button"
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-md mx-auto transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  isPathActive(section.basePath)
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground"
                )}
                title={section.label}
                onClick={() => {
                  if (section.landingPage) navigate(section.landingPage)
                }}
              >
                <section.icon className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (section.landingPage) navigate(section.landingPage)
                }}
                className={cn(
                  "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-left",
                  "hover:bg-accent/70 transition-colors",
                  isPathActive(section.basePath)
                    ? "bg-accent/50 text-foreground"
                    : "text-muted-foreground"
                )}
              >
                <section.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{section.label}</span>
              </button>
            )}

            {/* Expanded: sub-pages inline — only for the ACTIVE section, so a
                section's sub-tabs collapse when you're elsewhere (and the flat
                entity sections never show a dangling empty border stub). */}
            {!isCollapsed && isPathActive(section.basePath) && section.subPages.length > 0 && (
              <div className="ml-4 mt-1 space-y-1 border-l pl-4">
                {section.subPages.map((subPage) => {
                  const hasChildren = !!subPage.children && subPage.children.length > 0
                  // Active surface is the last path segment
                  // (/agents/<group>/<surface>, or /agents/overview); a bare
                  // /agents resolves to overview so page, sidebar and
                  // breadcrumb stay in sync.
                  const agentSegs = location.pathname.split("/").filter(Boolean)
                  const currentTab = (agentSegs[agentSegs.length - 1] !== "agents" ? agentSegs[agentSegs.length - 1] : "") || "overview"
                  // Resolve active from the current surface via matchTab.
                  const tabActive = subPage.matchTab ? subPage.matchTab.includes(currentTab) : null
                  const isOnChildRoute = hasChildren &&
                    (subPage.matchTab ? !!tabActive : location.pathname.startsWith(subPage.to))

                  return (
                    <div key={subPage.to}>
                      <NavLink
                        to={subPage.to}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
                            "hover:bg-accent hover:text-accent-foreground",
                            // Parent groups never take the solid highlight — only
                            // the active leaf does (matches Admin's Master Lists).
                            hasChildren
                              ? (isOnChildRoute ? "text-foreground font-medium" : "text-muted-foreground")
                              : (tabActive !== null ? tabActive : isActive)
                              ? "bg-accent text-accent-foreground font-medium"
                              : "text-muted-foreground"
                          )
                        }
                      >
                        <subPage.icon className="h-4 w-4" />
                        <span>{subPage.label}</span>
                      </NavLink>

                      {/* Nested children — the active group's surfaces (Agents)
                          or Master Lists' entities (Admin). */}
                      {hasChildren && isOnChildRoute && (
                        <div className="ml-4 mt-1 space-y-1 border-l pl-4">
                          {subPage.children!.map((child) => {
                            const childActive = child.matchTab ? child.matchTab.includes(currentTab) : null
                            return (
                              <NavLink
                                key={child.to}
                                to={child.to}
                                className={({ isActive }) =>
                                  cn(
                                    "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
                                    "hover:bg-accent hover:text-accent-foreground",
                                    (childActive !== null ? childActive : isActive)
                                      ? "bg-accent text-accent-foreground font-medium"
                                      : "text-muted-foreground"
                                  )
                                }
                              >
                                {child.icon ? <child.icon className="h-4 w-4" /> : <span className="w-4" />}
                                <span>{child.label}</span>
                              </NavLink>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Collapsed: flyout on hover */}
            {isCollapsed && hoveredSection === section.id && (
              <div
                className="absolute left-full bottom-auto z-50 pl-2"
                style={{ top: 0, maxHeight: "calc(100vh - 120px)" }}
                onMouseEnter={handleFlyoutMouseEnter}
                onMouseLeave={handleFlyoutMouseLeave}
              >
                <div className="w-56 rounded-md border bg-popover p-2 shadow-lg max-h-[70vh] overflow-y-auto">
                  <div className="mb-2 px-2 text-sm font-medium text-foreground">
                    {section.label}
                  </div>

                  {section.subPages.map((subPage) => {
                    const hasChildren = subPage.children && subPage.children.length > 0

                    if (hasChildren) {
                      const firstChild = subPage.children![0]
                      return (
                        <div key={subPage.to}>
                          <button
                            type="button"
                            onClick={() => { navigate(firstChild.to); setHoveredSection(null) }}
                            className="w-full px-3 py-2 text-sm text-muted-foreground flex items-center gap-2 hover:bg-accent hover:text-accent-foreground rounded-md transition-colors text-left"
                          >
                            <subPage.icon className="h-4 w-4" />
                            <span className="truncate">{subPage.label}</span>
                          </button>
                          <div className="ml-4 space-y-1">
                            {subPage.children!.map((child) => (
                              <NavLink
                                key={child.to}
                                to={child.to}
                                onClick={() => setHoveredSection(null)}
                                className={() =>
                                  cn(
                                    "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                                    "hover:bg-accent hover:text-accent-foreground",
                                    isSubPageActive(child.to)
                                      ? "bg-accent text-accent-foreground font-medium"
                                      : "text-muted-foreground"
                                  )
                                }
                              >
                                {child.icon ? <child.icon className="h-4 w-4" /> : <span className="w-4" />}
                                <span className="truncate">{child.label}</span>
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      )
                    }

                    return (
                      <NavLink
                        key={subPage.to}
                        to={subPage.to}
                        onClick={() => setHoveredSection(null)}
                        className={() =>
                          cn(
                            "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                            "hover:bg-accent hover:text-accent-foreground",
                            isSubPageActive(subPage.to)
                              ? "bg-accent text-accent-foreground font-medium"
                              : "text-muted-foreground"
                          )
                        }
                      >
                        <subPage.icon className="h-4 w-4" />
                        <span className="truncate">{subPage.label}</span>
                      </NavLink>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="shrink-0 border-t border-border/40 p-2">
        {/* Dark mode toggle */}
        <button
          type="button"
          onClick={toggle}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
            "hover:bg-accent hover:text-accent-foreground text-muted-foreground",
            isCollapsed && "justify-center px-0"
          )}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? (
            <Sun className="h-4 w-4 shrink-0" />
          ) : (
            <Moon className="h-4 w-4 shrink-0" />
          )}
          {!isCollapsed && <span>{isDark ? "Light Mode" : "Dark Mode"}</span>}
        </button>

        {/* Profile */}
        <button
          type="button"
          onClick={onProfileClick}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
            "hover:bg-accent hover:text-accent-foreground text-muted-foreground",
            isCollapsed && "justify-center px-0"
          )}
          title="Profile"
        >
          <User className="h-4 w-4 shrink-0" />
          {!isCollapsed && <span>Profile</span>}
        </button>

        {/* Logout */}
        <button
          type="button"
          onClick={onLogoutClick}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
            "hover:bg-accent hover:text-accent-foreground text-muted-foreground",
            isCollapsed && "justify-center px-0"
          )}
          title="Logout"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  )
}
